/* ============================================================
   ATELIER - HOLD TO PREVIEW FINISHES  (mobile, touch only)

   Long-press a product card -> its finish swatches FLIP-bloom from
   their spots on the card into an arc around the thumb -> drag toward
   a swatch to live-preview the product in that finish -> release ON a
   swatch to freeze into the committed action modal (Add to cart /
   View product / Wishlist / Add to project + a "More from the
   range" strip of real range pieces in that finish); release in the
   centre dead-zone or off-ring to cancel (swatches glide home).

   Pure progressive enhancement: only on touch + (hover:none) devices,
   only after a deliberate long-press that did not become a scroll. The
   finish dots still tap-navigate normally; nothing here blocks that.

   Styling: atelier-finish-bloom.css (.fb-*). Reuses the global
   ac-sw--<family> gradient classes + each dot's data-fimg (the same
   showcase image the desktop hover swap uses). Loaded site-wide from
   sections/atelier-header.liquid so it covers every .pc context.

   Haptics: see memory web-haptics-ios-research - Android gets real
   navigator.vibrate ticks; current iOS (26.5+) cannot fire haptics
   mid-drag, so there the feel is carried by the visuals. We still call
   vibrate() (no-op where unsupported) as a free progressive layer.
   ============================================================ */
(function () {
  'use strict';

  /* ---- on-device debug HUD (add ?fbdebug to the URL to switch on) ----
     Lets us see the gesture pipeline on a phone without Safari devtools.
     Remove this block + the dbg() calls once the feel is locked. */
  /* Off by default; append ?fbdebug to the URL to bring back the on-screen HUD. */
  var DEBUG = /[?&]fbdebug/.test(location.search);
  var armCount = 0, bloomRaf = 0, finCount = 0;
  var hud = null, hudLog = [];
  var dbgEpoch = (window.performance && performance.now) ? performance.now() : Date.now();
  function dbgMs() { return Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - dbgEpoch); }
  function dbg(msg) {
    if (!DEBUG) return;
    if (!hud) {
      hud = document.createElement('div');
      hud.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;' +
        'background:rgba(0,0,0,.86);color:#5f5;font:11px/1.4 ui-monospace,monospace;' +
        'padding:7px 9px;border-radius:7px;max-width:88vw;white-space:pre-wrap;pointer-events:none';
      (document.body || document.documentElement).appendChild(hud);
    }
    hudLog.push(dbgMs() + 'ms  ' + msg); if (hudLog.length > 14) hudLog.shift();
    hud.textContent = '[FB]\n' + hudLog.join('\n');
  }

  /* gate: touch device with no real hover (phones/tablets). Desktop untouched. */
  var TOUCH = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  var NOHOVER = window.matchMedia && window.matchMedia('(hover:none)').matches;
  dbg('loaded touch=' + TOUCH + ' nohover=' + NOHOVER);
  if (!TOUCH || !NOHOVER) { dbg('GATED OUT - not running'); return; }

  /* ---- styles, injected from JS ----
     Deliberately NOT a separate .css asset: a running `shopify theme dev` often
     skips newly-added asset files, which left the overlay unstyled+invisible AND
     left the native iOS image drag-lift un-suppressed. Shipping the CSS inside
     this (clearly-syncing) JS file removes that whole failure mode. Swatch
     gradients still come from the global ac-sw--<family> classes (already loaded
     via atelier-collection.css on card pages). */
  (function injectCSS() {
    if (document.getElementById('fb-style')) return;
    var css =
      '.fb-overlay{position:fixed;inset:0;z-index:99999;pointer-events:none;-webkit-tap-highlight-color:transparent}' +
      '.fb-overlay,.fb-overlay *{box-sizing:border-box}' +
      /* iOS steals a long-press on an image/link for its own drag-lift (the picture/text
         nudges larger) and that cancels our gesture. callout:none kills the menu but NOT
         the drag - this does. */
      '.pc img,.pc-im,.pc-im-link,.pc-im img,.pc a{-webkit-user-drag:none;-webkit-touch-callout:none}' +
      '.fb-scrim{position:fixed;inset:0;background:rgba(26,22,18,.5);opacity:0;transition:opacity .26s ease}' +
      '.fb-overlay.on .fb-scrim{opacity:1}' +
      /* away mode (Add to Project slid in): the stage dims further so the dark-glass
         panel text reads - a second layer fading on OPACITY (composited), not a
         background transition (full-screen repaint) and not blur (not an Atelier
         device + backdrop-filter unreliable in this store). */
      '.fb-scrim::after{content:"";position:absolute;inset:0;background:rgba(12,10,8,.6);opacity:0;transition:opacity .45s ease}' +
      '.fb-overlay.fb-away .fb-scrim::after{opacity:1}' +
      /* this store hides empty styled boxes (a:empty,div:empty{display:none}); our scrim/
         swatches/home-cue carry an <i></i> so they're never :empty, but this scoped override
         (0,2,0 > the global 0,1,1) is belt-and-suspenders. See memory app-css-gotchas. */
      '.fb-overlay .fb-scrim,.fb-overlay .fb-sw,.fb-overlay .fb-home{display:block}' +
      /* while a card is gliding its swatches home, neutralise its press-lift so a scrolling
         finger can not shift its dots (.is-press translateY(-4px)) out from under the clones.
         (0,4,0) beats the .is-press rule. */
      '.atelier-collection .pc.fb-hold.is-press{transform:none}' +
      /* focal "hero": the SPECIMEN PLAQUE - one composed object lifted out of the
         card: white image panel on top + a dark-glass info label fused beneath
         (brand / title / finish - price). transform is JS-driven (FLIP from the
         card rect -> showcase); opacity via .on. */
      /* translate (the separate CSS property) drives the Add-to-Project slide-away:
         it composes with the JS-driven `transform`, so the FLIP/tilt inline
         transforms never conflict with the stage exit. */
      '.fb-card{position:fixed;display:flex;flex-direction:column;background:transparent;border-radius:16px;box-shadow:0 26px 70px rgba(0,0,0,.34);opacity:0;transform-origin:top left;will-change:transform;translate:0 0;transition:opacity .26s ease,transform .46s cubic-bezier(.22,1,.36,1),translate .5s cubic-bezier(.45,.05,.25,1)}' +
      '.fb-overlay.on .fb-card{opacity:1}' +
      '.fb-imx{position:relative;width:100%;background:#fff;border-radius:16px 16px 0 0;overflow:hidden;perspective:780px}' +
      /* label home #2: ABOVE the image (column-reverse - no DOM moves) for
         holds where the classic fan would cross the below-image label */
      '.fb-card.plq-top{flex-direction:column-reverse}' +
      '.fb-card.plq-top .fb-imx{border-radius:0 0 16px 16px}' +
      '.fb-card.plq-top .fb-plq{border-radius:16px 16px 0 0;border-top:1px solid rgba(255,255,255,.15);border-bottom:0}' +
      /* tilt layer: the product subtly turns toward the thumb (parallax) */
      '.fb-tilt{position:absolute;inset:0;transform-style:preserve-3d;transition:transform .14s ease-out;will-change:transform}' +
      '.fb-im{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;opacity:0;transition:opacity .26s ease}' +
      '.fb-im.on{opacity:1}' +
      /* the fused glass label (same dark-glass language as the project panel) */
      '.fb-plq{background:rgba(15,13,11,.58);border:1px solid rgba(255,255,255,.15);border-top:0;border-radius:0 0 16px 16px;padding:12px 15px 13px;font-family:"Sora",system-ui,sans-serif;color:#fff;text-align:left}' +
      '.fb-plq .eb{display:block;font-size:9.5px;font-weight:600;letter-spacing:.15em;text-transform:uppercase;opacity:.62}' +
      /* title WRAPS (Chris: never truncate) - the whole stack is laid out from
         the plaque's MEASURED height; 3-line clamp is only a monster backstop */
      '.fb-plq b{display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;font-size:14.5px;font-weight:600;line-height:1.3;margin-top:3px;overflow:hidden}' +
      '.fb-plq .plrow{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin-top:7px}' +
      '.fb-plq .fin{font-style:normal;font-size:12.5px;font-weight:600;opacity:.88;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
      '.fb-plq em{font-style:normal;font-size:13.5px;font-weight:600;white-space:nowrap}' +
      '.fb-plq em s{opacity:.5;font-weight:400;margin-right:7px}' +
      '.fb-plq em i.so{font-style:normal;font-weight:600;color:#ff8a8a}' +
      /* NO css transition on .fb-sw: a CSS transition + the bloom WebAnimation both
         owning `transform` made the bloom replay (the phantom second flight). The
         drag-select transition is added INLINE only after the bloom finishes. */
      '.fb-sw{position:fixed;left:0;top:0;width:44px;height:44px;border-radius:50%;border:2.5px solid #fff;outline:1px solid rgba(29,31,35,.12);box-shadow:0 5px 16px rgba(0,0,0,.3);background-size:cover;background-position:center;will-change:transform;translate:0 0;transform:translate3d(0,0,0) scale(1)}' +
      '.fb-sw.sel{box-shadow:0 0 0 3px #c41230,0 8px 22px rgba(0,0,0,.4)}' + // brand red
      '.fb-home{position:fixed;left:0;top:0;width:46px;height:46px;border-radius:50%;transform:translate(-50%,-50%);border:1.5px dashed rgba(255,255,255,.55);opacity:0;transition:opacity .2s ease}' +
      '.fb-overlay.on.has-sel .fb-home{opacity:.6}' +
      /* committed state: release on a finish freezes the preview into an action
         modal. The four actions form a fixed-width 2x2 GRID - equal cells, one
         type size, aligned edges - anchored toward the BOTTOM of the screen
         with the range strip (thumb zone), clear of the product zone above. */
      '.fb-tray{position:fixed;left:0;top:0;z-index:4;transform:translateX(-50%);width:min(calc(100vw - 32px),368px);display:grid;grid-template-columns:1fr 1fr;gap:10px;opacity:0;translate:0 0;transition:opacity .3s ease .06s,translate .5s cubic-bezier(.45,.05,.25,1);pointer-events:none}' +
      '.fb-tr{display:contents}' + // rows exist for wiring; the grid sees the four buttons
      '.fb-btn{-webkit-appearance:none;appearance:none;border:0;width:100%;min-height:46px;font:600 13.5px/1.2 "Sora",system-ui,sans-serif;letter-spacing:.01em;padding:12px 10px;border-radius:13px;display:inline-flex;align-items:center;justify-content:center;gap:8px;white-space:nowrap;cursor:pointer}' +
      '.fb-overlay.fb-compact .fb-btn{min-height:42px;padding:10px 8px;font-size:12.5px}' +
      '.fb-btn--add{background:#c41230;color:#fff;box-shadow:0 8px 22px rgba(196,18,48,.42)}' +
      '.fb-btn--add.ok{background:#2c7a48;box-shadow:none}' +
      '.fb-btn--view,.fb-btn--save,.fb-btn--proj{background:rgba(255,255,255,.15);color:#fff;border:1px solid rgba(255,255,255,.34)}' +
      '.fb-btn svg{width:16px;height:16px;flex:0 0 auto}' +
      '.fb-btn--save svg{fill:none;stroke:#fff;stroke-width:1.7}' +
      '.fb-btn--save.ok{background:rgba(255,255,255,.28)}' +
      '.fb-btn--save.ok svg{fill:#fff;stroke:none}' +
      '.fb-btn--proj svg{fill:none;stroke:#fff;stroke-width:1.7;stroke-linejoin:round}' +
      '.fb-x{position:fixed;z-index:5;top:calc(env(safe-area-inset-top,0px) + 14px);right:14px;left:auto;width:40px;height:40px;border-radius:50%;border:0;background:rgba(255,255,255,.16);color:#fff;font:300 24px/1 system-ui;display:flex;align-items:center;justify-content:center;cursor:pointer;opacity:0;transition:opacity .3s ease;pointer-events:none}' +
      '.fb-overlay.committed .fb-tray,.fb-overlay.committed .fb-x{opacity:1;pointer-events:auto}' +
      '.fb-overlay.committed .fb-scrim,.fb-overlay.committed .fb-sw{pointer-events:auto}' +
      /* "More from the range" strip: real range pieces (finish-matched by the
         rail section) as compact tiles, lightbox-style, + a View-the-range CTA */
      '.fb-range{position:fixed;left:0;right:0;top:0;z-index:4;opacity:0;pointer-events:none;translate:0 0;transition:opacity .32s ease .12s,translate .5s cubic-bezier(.45,.05,.25,1)}' +
      '.fb-overlay.committed .fb-range.has{opacity:1;pointer-events:auto}' +
      '.fb-range-h{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 18px;margin-bottom:8px}' +
      '.fb-range-h span{font:600 10.5px/1.2 "Sora",system-ui,sans-serif;letter-spacing:.14em;text-transform:uppercase;color:#fff;opacity:.66;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
      '.fb-range-cta{flex:0 0 auto;font:600 12px/1 "Sora",system-ui,sans-serif;color:#fff;text-decoration:none;background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.34);padding:9px 14px;border-radius:999px;display:inline-flex;align-items:center;gap:6px}' +
      '.fb-range-cta svg{width:13px;height:13px;fill:none;stroke:#fff;stroke-width:2}' +
      /* touch-action:pan-x = this touch can ONLY pan the strip horizontally, so
         exempting it from the overlay's preventDefault can never leak a page scroll */
      '.fb-range-s{display:flex;gap:10px;overflow-x:auto;padding:2px 18px 8px;touch-action:pan-x;overscroll-behavior-x:contain;-webkit-overflow-scrolling:touch;scrollbar-width:none}' +
      '.fb-range-s::-webkit-scrollbar{display:none}' +
      '.fb-rc{flex:0 0 auto;width:88px;text-decoration:none;-webkit-tap-highlight-color:transparent}' +
      '.fb-rc span{display:block;width:88px;height:88px;border-radius:12px;background:#fff;overflow:hidden;border:1px solid rgba(255,255,255,.22)}' +
      '.fb-rc img{width:100%;height:100%;object-fit:cover;display:block}' +
      '.fb-rc b{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font:500 10px/1.3 "Sora",system-ui,sans-serif;color:rgba(255,255,255,.85);margin-top:6px;text-align:center}' +
      '.fb-rc em{display:block;font:600 11px/1 "Sora",system-ui,sans-serif;font-style:normal;color:#fff;text-align:center;margin-top:3px}' +
      /* Add-to-Project slide-away: the committed content exits stage-left while
         the .apa--bloom panel slides in from the right on the SAME dark scrim.
         The scrim + overlay never tear down; closing the panel slides it back. */
      '.fb-overlay.fb-away .fb-card,.fb-overlay.fb-away .fb-tray,.fb-overlay.fb-away .fb-range,.fb-overlay.fb-away .fb-sw{translate:-108vw 0}' +
      '.fb-overlay.fb-away .fb-x{opacity:0;pointer-events:none}';
    var st = document.createElement('style');
    st.id = 'fb-style';
    st.textContent = css;
    (document.head || document.documentElement).appendChild(st);
    dbg('css injected');
  })();

  /* ---- tunables ---- */
  var LONGPRESS_MS = 340;   // hold time before the bloom arms
  var CONFIRM_MS   = 180;   // bloom must stay OPEN this long before a hold counts as
                            // "seen" and retires the discovery hints (a 360ms tap that
                            // flashed 10ms of bloom must NOT count - Chris 2026-07-13)
  var MOVE_CANCEL  = 10;    // px of pre-arm movement that means "this is a scroll"
  var RING_R       = 96;    // ring radius from the press point
  var SW            = 44;   // swatch clone diameter (must match CSS)
  var DEADZONE     = 46;    // radius around centre = cancel / home
  var OUTER_R      = 152;   // beyond this radius from the press point = let go (deselect)
  var ANG_TOL      = 0.5;   // rad (~28deg) - finger must point roughly AT a swatch to select it
  var MAX_FINS     = 8;     // ring cap (rare to exceed)
  var STEP         = 0.70;  // ~40deg between swatches
  var MAX_ARC      = 3.0;   // ~172deg - keep the fan to one side of the finger
  var EDGE_M       = 124;   // px from screen edge where the fan starts leaning inward
  var BLOOM_MS     = 380;   // per-swatch fly-out duration (Chris 2026-07-13: 480
                            // read as sluggish once the accelerate-from-rest
                            // easing made the whole flight visible)
  var BLOOM_STAGGER = 38;   // ms between each swatch launching (the cascade)
  var BLOOM_ARC    = 30;    // px the flight path bows outward (0 = straight line)

  /* ---- state ---- */
  var S = null;             // active gesture session, or null
  var armTimer = 0;
  var heldCard = null;      // card whose press-lift is neutralised during a glide-home
  var overlay = null, scrim = null, focal = null, home = null, nameEl = null;
  var focalA = null, focalB = null, focalCur = null; // two stacked imgs for cross-fade
  var tilt = null; // parallax layer inside the hero
  var imx = null;  // the plaque's white image panel (tilt + drift live here)
  var tray = null, addBtn = null, viewBtn = null, xBtn = null; // committed-state action UI
  var saveBtn = null, projBtn = null;                          // committed-state row 2 (wishlist / project)
  var railEl = null, railHead = null, railCta = null, railStrip = null; // "More from the range" strip
  var railCache = {};      // product handle -> parsed rail data (or {empty:true})
  var savedSet = null;     // wishlist handles, seeded from SWishlist.getList() on first commit
  var apaLoadP = null, apaState = 0; // Add-to-Project lazy bundle: 0 unknown, 1 loading, 2 lazy-loaded

  function vibrate(ms) { try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) {} }

  /* ---------- overlay (built once, reused) ---------- */
  function buildOverlay() {
    overlay = document.createElement('div');
    overlay.className = 'fb-overlay';
    /* NB: the <i></i> fillers are load-bearing - this store hides empty styled
       divs (div:empty{display:none}); content keeps the rule from matching. */
    scrim = document.createElement('div'); scrim.className = 'fb-scrim'; scrim.innerHTML = '<i></i>';
    focal = document.createElement('div'); focal.className = 'fb-card';
    imx = document.createElement('div'); imx.className = 'fb-imx';
    tilt = document.createElement('div'); tilt.className = 'fb-tilt';
    focalA = document.createElement('img'); focalA.className = 'fb-im on'; focalA.alt = '';
    focalB = document.createElement('img'); focalB.className = 'fb-im'; focalB.alt = '';
    tilt.appendChild(focalA); tilt.appendChild(focalB); imx.appendChild(tilt); focal.appendChild(imx); focalCur = focalA;
    /* the fused glass label - lives INSIDE the plaque object, rides every flight */
    nameEl = document.createElement('div'); nameEl.className = 'fb-plq';
    nameEl.innerHTML = '<span class="eb"></span><b></b><span class="plrow"><i class="fin"></i><em></em></span>';
    focal.appendChild(nameEl);
    home = document.createElement('div'); home.className = 'fb-home'; home.innerHTML = '<i></i>';
    tray = document.createElement('div'); tray.className = 'fb-tray';
    var tr1 = document.createElement('div'); tr1.className = 'fb-tr';
    addBtn = document.createElement('button'); addBtn.type = 'button'; addBtn.className = 'fb-btn fb-btn--add'; addBtn.textContent = 'Add to cart';
    viewBtn = document.createElement('button'); viewBtn.type = 'button'; viewBtn.className = 'fb-btn fb-btn--view'; viewBtn.textContent = 'View product';
    tr1.appendChild(addBtn); tr1.appendChild(viewBtn);
    var tr2 = document.createElement('div'); tr2.className = 'fb-tr fb-tr--sub';
    /* heart = the site's wishlist glyph (atelier-wishlist-toast.js); folder = the
       Add-to-Project panel's own header glyph - same icons the user meets elsewhere */
    saveBtn = document.createElement('button'); saveBtn.type = 'button'; saveBtn.className = 'fb-btn fb-btn--save';
    saveBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M12 21s-7.5-4.8-9.5-9.1C.9 8.2 3 4.5 6.7 4.5c2.2 0 3.9 1.2 5.3 3 1.4-1.8 3.1-3 5.3-3 3.7 0 5.8 3.7 4.2 7.4C19.5 16.2 12 21 12 21z"/></svg><span>Wishlist</span>';
    projBtn = document.createElement('button'); projBtn.type = 'button'; projBtn.className = 'fb-btn fb-btn--proj';
    projBtn.setAttribute('data-apg-add-to-project', '');
    projBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M3 7h7l2 2h9v9a2 2 0 0 1-2 2H3z"/></svg><span>Add to project</span>';
    /* PDP: the panel's delegated listener prefers its own curHandle (the page's
       product) over our data-product-handle, so it would file the WRONG product.
       PDP blooms only happen on enriched rail cards - hide the action there
       (the PDP has its own Add-to-Project button anyway). */
    if (document.querySelector('.atelier-pdp')) projBtn.style.display = 'none';
    tr2.appendChild(saveBtn); tr2.appendChild(projBtn);
    tray.appendChild(tr1); tray.appendChild(tr2);
    xBtn = document.createElement('button'); xBtn.type = 'button'; xBtn.className = 'fb-x'; xBtn.innerHTML = '&times;'; xBtn.setAttribute('aria-label', 'Close');
    railEl = document.createElement('div'); railEl.className = 'fb-range';
    railEl.innerHTML = '<div class="fb-range-h"><span></span>' +
      '<a class="fb-range-cta" href="#">View the range <svg viewBox="0 0 24 24"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg></a></div>' +
      '<div class="fb-range-s"><i></i></div>'; // <i> filler: store div:empty rule
    railHead = railEl.querySelector('.fb-range-h span');
    railCta = railEl.querySelector('.fb-range-cta');
    railStrip = railEl.querySelector('.fb-range-s');
    overlay.appendChild(scrim);
    overlay.appendChild(focal);
    overlay.appendChild(home);
    overlay.appendChild(tray);
    overlay.appendChild(railEl);
    overlay.appendChild(xBtn);
    document.body.appendChild(overlay);

    /* committed-state taps (the overlay is pointer-events:none until committed) */
    function guarded(fn) { return function (e) { if (S && S.committed && !S.ignoreClicks) fn(e); }; }
    scrim.addEventListener('click', guarded(function () {
      if (S.away) { apaRequestClose(); return; } // slid away: scrim = close the panel, come back
      closeCommitted();
    }));
    xBtn.addEventListener('click', function () { if (S && S.committed) closeCommitted(); });
    viewBtn.addEventListener('click', guarded(function () { if (S.chosen && S.chosen.href) window.location.href = S.chosen.href; }));
    addBtn.addEventListener('click', guarded(function () { if (S.chosen) addToCart(S.chosen); }));
    saveBtn.addEventListener('click', guarded(function () { if (S.chosen) saveFinish(S.chosen); }));
    projBtn.addEventListener('click', onProjTap);
    overlay.addEventListener('click', guarded(function (e) {
      var sw = e.target.closest ? e.target.closest('.fb-sw') : null;
      if (!sw) return;
      for (var i = 0; i < S.fins.length; i++) { if (S.fins[i].el === sw) { selectCommitted(i); break; } }
    }));
    /* the strip's links navigate natively - kill the synthetic release-click
       before it can fire an unintended navigation (capture beats the <a>) */
    railEl.addEventListener('click', function (e) {
      if (S && S.ignoreClicks) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  }

  /* the on-screen bottom of the brand header - we keep it clear of the scrim and
     never place UI under it (the header is brand recognition, not to be shaded). */
  function headerBottom() {
    var el = document.querySelector('.section-atelier-header .at-hdr') ||
             document.querySelector('.at-hdr') ||
             document.querySelector('.section-atelier-header');
    if (!el) return 0;
    var r = el.getBoundingClientRect();
    return (r.bottom > 0 && r.bottom < window.innerHeight * 0.45) ? r.bottom : 0;
  }

  /* cross-fade the focal image to a new finish (no hard cut) */
  function setFocalImage(src) {
    var back = (focalCur === focalA) ? focalB : focalA;
    if ((back.getAttribute('src') || '') !== src) back.src = src;
    back.classList.add('on');
    focalCur.classList.remove('on');
    focalCur = back;
  }
  function priceHTML(fin) {
    if (!fin) return '';
    if (fin.soldout) return '<i class="so">Currently unavailable</i>';
    var s = fin.price || '';
    if (fin.compare) s = '<s>' + fin.compare + '</s>' + s;
    return s;
  }
  /* plaque label = brand eyebrow / product TITLE (set once per session by
     arm()) / finish - price row. Only finish+price change while scrubbing. */
  function setCaption(eyebrow, finish, fin) {
    nameEl.querySelector('.eb').textContent = eyebrow || '';
    nameEl.querySelector('.fin').textContent = finish || '';
    nameEl.querySelector('em').innerHTML = priceHTML(fin);
    /* colour siblings are SEPARATE products ("… Brushed Gold" vs "… Chrome") -
       the plaque title follows the previewed finish, not the held card.
       Real fins always carry ptitle (arm() fills a card-title fallback in);
       arm's prelim pseudo-fin has none, keeping its just-set card title. */
    if (fin && fin.ptitle !== undefined) setCaptionTitle(fin.ptitle);
  }
  function setCaptionTitle(title) { nameEl.querySelector('b').textContent = title || ''; }
  /* parallax: the product turns subtly toward the thumb as it moves */
  function applyTilt(x, y) {
    if (!tilt) return;
    var nx = (x / window.innerWidth - 0.5) * 2;  // -1..1
    var ny = (y / window.innerHeight - 0.5) * 2;
    tilt.style.transform = 'rotateX(' + (-ny * 6).toFixed(2) + 'deg) rotateY(' + (nx * 8).toFixed(2) + 'deg)';
  }
  function resetTilt() { if (tilt) tilt.style.transform = ''; }
  /* hide the card's real dots while their clones are lifted, so it reads as the
     SAME swatches leaving the card. Hide is instant (a clone takes the dot's exact
     place); restore FADES them + the +N chip back in so nothing pops. */
  function hideCardDots(card) {
    var dd = card.querySelectorAll('.pc-fin, .pc-fin-more');
    for (var i = 0; i < dd.length; i++) { dd[i].style.transition = 'none'; dd[i].style.opacity = '0'; }
    return dd;
  }
  function showCardDots(dd) {
    if (!dd) return;
    for (var i = 0; i < dd.length; i++) {
      (function (el) {
        el.style.transition = 'opacity .22s ease';
        el.style.opacity = '1';
        window.setTimeout(function () { el.style.transition = ''; el.style.opacity = ''; }, 260);
      })(dd[i]);
    }
  }
  function setAddBtn(f) {
    addBtn.classList.remove('ok');
    if (f && f.soldout) { addBtn.disabled = true; addBtn.textContent = 'Sold out'; }
    else { addBtn.disabled = false; addBtn.textContent = 'Add to cart'; }
  }
  /* While the swatches glide home, the user can scroll - which moves the in-flow card/dots
     but NOT our position:fixed clones, so they'd land on stale air. JS scroll-tracking
     lags on iOS (scroll fires after paint); a transform-based containing block ALSO lags
     (it makes a composited layer iOS momentum-scrolls separately). The only pixel-perfect
     way: re-anchor the overlay to the DOCUMENT (position:absolute at the current scroll)
     and make the tracked children position:ABSOLUTE (done in close()), so they're plain
     document content that scrolls natively with the page - NO transform, NO JS, no lag. */
  function startGlideScroll() {
    if (!overlay) return;
    overlay.style.position = 'absolute';
    overlay.style.left = window.pageXOffset + 'px';
    overlay.style.top = window.pageYOffset + 'px';
    overlay.style.right = 'auto'; overlay.style.bottom = 'auto'; // CSS inset:0 would over-constrain
    overlay.style.width = window.innerWidth + 'px';
    overlay.style.height = window.innerHeight + 'px';
    // NB: intentionally NO transform (a composited layer lags during iOS momentum scroll).
  }
  function stopGlideScroll() {
    if (!overlay) return;
    overlay.style.position = '';
    overlay.style.left = ''; overlay.style.top = '';
    overlay.style.right = ''; overlay.style.bottom = '';
    overlay.style.width = ''; overlay.style.height = '';
    overlay.style.transform = '';
    if (focal) focal.style.position = ''; // back to CSS position:fixed for the next bloom
  }
  function releaseHeld() { if (heldCard) { heldCard.classList.remove('fb-hold'); heldCard = null; } }

  /* ---------- geometry: half-fan of slots around (cx,cy) ----------
     fanDown=true -> the fan opens DOWNWARD (swatches below the finger), used when
     the hero is above; false -> opens UPWARD. Always a <=180deg fan on ONE side of
     the finger so it never crosses to the hero's side.
     The ring is THE gesture language and never changes shape for furniture:
     the plaque's LABEL is what relocates instead (above/below the image,
     decided at arm time - see the plq-top logic in arm()). */
  function computeSlots(cx, cy, n, fanDown, topLimit) {
    var vw = window.innerWidth, vh = window.innerHeight;
    var pad = SW / 2 + 8;
    var R = RING_R;
    var arc = n > 1 ? Math.min((n - 1) * STEP, MAX_ARC) : 0;
    var half = arc / 2;
    var center = fanDown ? (Math.PI / 2) : (-Math.PI / 2); // straight up (default) - 0=right, +PI/2=down

    /* EDGE TILT: directly-above is the default, but near a L/R edge a straight-up fan
       shoves the outer swatches off-screen (where the old code CLAMPED them into a stack).
       Instead, rotate the WHOLE arc toward the open side by exactly enough to keep the
       binding (outermost) swatch at the screen margin - so nothing stacks. The amount
       grows smoothly as the finger nears the edge; mid-screen it's 0 (straight up).
       (fan is always up in practice; fanDown left untouched.) */
    if (!fanDown) {
      /* Apply only the constraint for the half the finger is in - applying BOTH let the
         right-edge ceiling (which sits near -86deg even far from the right edge) clobber a
         valid left-edge tilt. */
      if (cx < vw / 2) {
        // near the LEFT edge -> the most-counter-clockwise swatch (center - half) must keep x >= pad
        var minCosL = Math.max(-1, Math.min(1, (pad - cx) / R));
        var cMinL = half - Math.acos(minCosL);       // floor on center: tilt RIGHT this much
        if (center < cMinL) center = cMinL;
      } else {
        // near the RIGHT edge -> the most-clockwise swatch (center + half) must keep x <= vw - pad
        var maxCosR = Math.max(-1, Math.min(1, ((vw - pad) - cx) / R));
        var cMaxR = -Math.acos(maxCosR) - half;      // ceiling on center: tilt LEFT this much
        if (center > cMaxR) center = cMaxR;
      }
    }

    var span = n > 1 ? arc / (n - 1) : 0;
    var slots = [];
    for (var i = 0; i < n; i++) {
      var a = center - half + i * span;
      var x = cx + R * Math.cos(a);
      var y = cy + R * Math.sin(a);
      x = Math.max(pad, Math.min(vw - pad, x));                    // final safety only
      y = Math.max((topLimit || 0) + pad, Math.min(vh - pad, y));  // keep clear of the header
      slots.push({ a: a, x: x, y: y });
    }
    return slots;
  }

  /* circular nearest: the slot whose angle is closest to `ang`, plus that gap */
  function nearestByAngle(slots, ang) {
    var best = 0, bestD = Infinity;
    for (var i = 0; i < slots.length; i++) {
      var d = Math.abs(Math.atan2(Math.sin(ang - slots[i].a), Math.cos(ang - slots[i].a)));
      if (d < bestD) { bestD = d; best = i; }
    }
    return { idx: best, d: bestD };
  }

  /* ---------- shared showcase geometry (preview AND committed) ----------
     One layout for the hero across both phases, so the release-freeze never
     moves or resizes the image. Top-down stack budget: hero / caption /
     swatch row / 2-row tray / range strip; the hero absorbs the height
     budget, the stack compacts on short screens, and only when even a
     compact stack can't fit a usable hero does the strip drop. */
  function showcaseGeom(headTop, plqH) {
    var vw = window.innerWidth, vh = window.innerHeight;
    var hTop = Math.max(vh * 0.07, (headTop || 0) + 16);
    /* capB = the glass label's MEASURED height (titles wrap, never truncate) + clear gap */
    var plq = plqH || 84;
    /* the +34/+28 gap is measured to the swatch-row CENTRES - the SELECTED
       swatch's scaled radius (22*1.15) plus its crimson ring (~3.5) reach
       ~29px, leaving a real visible gap below the plaque */
    var capB = plq + 34, rowB = 58, trayB = 104, railGap = 14, railB = 176, botB = 12;
    var avail = vh - hTop;
    var railOn = true;
    var hw = Math.min(vw * 0.6, 300, avail - (capB + rowB + trayB + railGap + railB + botB));
    if (hw < 132) { // short screen: tighten the stack before giving up on the strip
      capB = plq + 28; rowB = 52; trayB = 96; railGap = 10;
      hw = Math.min(vw * 0.6, 300, avail - (capB + rowB + trayB + railGap + railB + botB));
    }
    if (hw < 116) { // still no room: no strip, back to the roomy spacing
      railOn = false; capB = plq + 34; rowB = 58; trayB = 104;
      hw = Math.min(vw * 0.6, 300, avail - (capB + rowB + trayB + botB));
    }
    hw = Math.max(96, hw);
    return { hTop: hTop, hw: hw, capB: capB, rowB: rowB, trayB: trayB, railGap: railGap, railOn: railOn, compact: rowB === 52 };
  }

  /* ---------- arm: build the bloom from the pressed card ---------- */
  function arm(card, tx, ty) {
    /* collect finishes from the real dots (hidden overflow dots included -
       they are display:none but still in the DOM and carry data-fimg). */
    var dots = Array.prototype.slice.call(card.querySelectorAll('.pc-fin'));
    if (dots.length < 2) return false; // nothing to preview
    if (dots.length > MAX_FINS) dots = dots.slice(0, MAX_FINS);
    demoStop(); // a real bloom replaces a playing demo guide instantly

    /* THE -4px LANDING BUG: the card is lifted by `.pc.is-press{transform:translateY(-4px)}`
       during the press, so every measurement below is taken 4px high -> clones land 4px
       above the resting dots. Take over the press: flag fb-on (stops .is-press re-applying)
       and snap the lift off WITHOUT its .35s transition so we measure the RESTING card. */
    document.documentElement.classList.add('fb-on');
    if (card.classList.contains('is-press')) {
      card.style.transition = 'none';
      card.classList.remove('is-press');
      void card.offsetWidth;       // force the transform to clear synchronously, now
      card.style.transition = '';
    }

    var imEl = card.querySelector('.pc-im');
    var mainImg = card.querySelector('.pc-im img.main');
    var chip = card.querySelector('.pc-fin-more');
    var chipR = chip ? chip.getBoundingClientRect() : null;
    var imR = imEl ? imEl.getBoundingClientRect() : card.getBoundingClientRect();

    if (!overlay) buildOverlay();
    stopGlideScroll(); // clear any leftover glide-scroll transform before measuring/placing
    releaseHeld();      // clear any leftover press-lift hold from an interrupted glide

    /* CONTAINING-BLOCK GOTCHA (see memory): the store transforms an ancestor of <body>,
       so our position:fixed overlay is NOT pinned to the viewport - it renders offset by
       the scroll amount. Measure that offset and subtract it from every fixed placement so
       the clones land EXACTLY on the real dots (this is the -4px landing bug). Zero when
       there's no offset, so always safe. */
    var oR = overlay.getBoundingClientRect();
    var oX = oR.left, oY = oR.top;

    /* keep the brand header clear of the scrim (don't shade it away) */
    var headTop = headerBottom();
    scrim.style.top = (headTop - oY) + 'px';
    resetTilt();

    /* === hero: ALWAYS the top showcase spot (Chris 2026-07-14) ===
       Same geometry the committed modal uses, so the release-freeze never
       moves or resizes the image. The old larger-clear-band placement either
       shrank the hero toward invisibility or dropped it below a high finger
       (under the hand); a high finger's fan overlapping the hero's lower
       edge is the accepted tradeoff - the swatches paint above it. */
    var vw = window.innerWidth;

    /* Populate the plaque label FIRST (from the active dot's own data), at a
       preliminary width, so its REAL height - titles wrap, never truncate -
       can drive the geometry. If the measured label is taller than the
       1-line estimate, the height budget is recomputed (the hero cedes the
       extra lines) and the label re-measured once at the refined width. */
    var brandTxt = ''; var vd = card.querySelector('.pc-vd'); if (vd) brandTxt = vd.textContent.trim();
    var ttEl = card.querySelector('.pc-tt');
    setCaptionTitle(ttEl ? ttEl.textContent.trim() : '');
    setCaption(brandTxt, dots[0].getAttribute('title') || '', {
      price: dots[0].getAttribute('data-price') || '',
      compare: dots[0].getAttribute('data-compare') || '',
      soldout: dots[0].getAttribute('data-soldout') === '1'
    });
    /* the plaque title now FOLLOWS the previewed finish, and sibling titles
       can wrap to different line counts - reserve the TALLEST one so no
       mid-preview swap ever pushes the plaque into the committed swatch row */
    var sibTitles = [];
    for (var sti = 0; sti < dots.length; sti++) {
      var spt = dots[sti].getAttribute('data-ptitle');
      if (spt) sibTitles.push(spt);
    }
    function plqMaxH() {
      var b = nameEl.querySelector('b'), keep = b.textContent, h = nameEl.offsetHeight || 84;
      for (var ti = 0; ti < sibTitles.length; ti++) {
        if (sibTitles[ti] === keep) continue;
        b.textContent = sibTitles[ti];
        var hh = nameEl.offsetHeight;
        if (hh > h) h = hh;
      }
      b.textContent = keep;
      return h;
    }
    var g = showcaseGeom(headTop, 84);
    focal.style.width = g.hw + 'px';
    var plqH = plqMaxH();
    if (Math.abs(plqH - 84) > 3) {
      g = showcaseGeom(headTop, plqH);
      focal.style.width = g.hw + 'px';
      plqH = plqMaxH() || plqH;   // narrower panel may wrap one more line
      g = showcaseGeom(headTop, plqH);
    }
    var hw = g.hw;
    var hero = { left: (vw - hw) / 2, top: g.hTop, w: hw };

    focal.style.left = (hero.left - oX) + 'px';
    focal.style.top = (hero.top - oY) + 'px';
    focal.style.width = hw + 'px';
    imx.style.height = hw + 'px'; // square image panel; the glass plaque adds itself
    var flipBack = 'translate(' + (imR.left - hero.left) + 'px,' + (imR.top - hero.top) +
                   'px) scale(' + (imR.width / hw) + ')'; // a delta - offset-free
    focal.style.transition = 'none';
    focal.style.transform = flipBack; // placed over the card for this frame

    var origSrc = mainImg ? (mainImg.currentSrc || mainImg.getAttribute('src')) : '';
    focalA.src = origSrc; focalA.classList.add('on');
    focalB.classList.remove('on'); focalB.removeAttribute('src');
    focalCur = focalA;
    /* LABEL HOME (Chris's model): below the image by DEFAULT; flip ABOVE the
       image only when the classic fan - a fixed band just above the finger -
       would cross the below home. The two homes are perfect complements:
       finger low = below home out of reach; finger at label height = fan
       covers below BUT cannot reach above the image; finger over the image =
       fan tops out above the below home, which is clear again. The ring
       itself NEVER changes shape - the words move, the gesture doesn't.
       Orientation holds for the whole session (incl. committed - the object's
       total height is identical either way, so the stack maths don't care). */
    var reach = RING_R + SW / 2 + 20;                   // how far above the finger the fan reaches
    var labelTopY = hero.top + hw;                      // below-home band = [labelTopY, labelTopY+plqH]
    var plqTop = (ty - reach) < (labelTopY + plqH + 12) && ty > (labelTopY + 12);
    focal.classList.toggle('plq-top', plqTop);

    var slots = computeSlots(tx, ty, dots.length, false, headTop); // the one classic fan, everywhere

    /* build swatch clones, FLIP from each dot's start rect to its slot */
    var fins = [];
    var frag = document.createDocumentFragment();
    for (var i = 0; i < dots.length; i++) {
      var dot = dots[i];
      var r = dot.getBoundingClientRect();
      var sx, sy;
      if (r.width > 0) { sx = r.left + r.width / 2; sy = r.top + r.height / 2; }
      else if (chipR) { sx = chipR.left + chipR.width / 2; sy = chipR.top + chipR.height / 2; } // hidden dot -> from +N chip
      else { sx = imR.left + imR.width / 2; sy = imR.top + imR.height; }
      var fromCard = r.width > 0;                          // true = a visible card dot; false = overflow (+N)
      var dotScale = fromCard ? (r.width / SW) : 0.44;     // clone starts/ends at the real dot's size

      var el = document.createElement('div');
      el.className = 'fb-sw';
      el.innerHTML = '<i></i>'; // keep it off the store's div:empty{display:none} rule
      /* gradient: reuse the global ac-sw--<family> class if present, else copy the dot's own background */
      var fam = '';
      for (var c = 0; c < dot.classList.length; c++) {
        if (dot.classList[c].indexOf('ac-sw--') === 0) { fam = dot.classList[c]; break; }
      }
      if (fam) el.classList.add(fam);
      var bg = dot.style.cssText; // inline background-image / background for image+hex fallbacks
      if (bg) el.style.cssText += ';' + bg;

      var slot = slots[i];
      el.style.left = (slot.x - SW / 2 - oX) + 'px';
      el.style.top = (slot.y - SW / 2 - oY) + 'px';
      var dx = sx - slot.x, dy = sy - slot.y;
      /* start sitting exactly on its real dot (same place + size, NO transition so the
         placement is instant), so when the real dot is hidden the clone IS the dot. */
      el.style.transition = 'none';
      el.style.transform = 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(' + dotScale + ')';

      fins.push({
        el: el, slot: slot, a: slot.a, dx: dx, dy: dy, dotScale: dotScale, fromCard: fromCard, dot: dot, // a: angle used by nearestByAngle()
        href: dot.getAttribute('href') || '',
        fimg: dot.getAttribute('data-fimg') || '',
        name: dot.getAttribute('title') || dot.getAttribute('aria-label') || '',
        ptitle: dot.getAttribute('data-ptitle') || (ttEl ? ttEl.textContent.trim() : ''),
        price: dot.getAttribute('data-price') || '',
        compare: dot.getAttribute('data-compare') || '',
        soldout: dot.getAttribute('data-soldout') === '1'
      });
      frag.appendChild(el);
    }
    /* clear any clones from a previous session, then mount the new ring */
    var old = overlay.querySelectorAll('.fb-sw');
    for (var k = 0; k < old.length; k++) old[k].remove();
    overlay.appendChild(frag);

    /* preload the finish images for instant preview swaps - but only AFTER the
       cascade has landed. Fetch+decode during the launch frames stole enough
       main-thread/raster time that the 46ms stagger aliased into PAIRS of
       swatches launching per painted frame (Chris: "they move in lots of 2").
       Worst case now: a drag onto a swatch in the first second cross-fades a
       beat later. */
    window.setTimeout(function () {
      if (!S) return;
      for (var p = 0; p < S.fins.length; p++) {
        if (S.fins[p].fimg) { var im = new Image(); im.src = S.fins[p].fimg; }
      }
    }, BLOOM_MS + (fins.length - 1) * BLOOM_STAGGER + 120);

    home.style.left = (tx - oX) + 'px';
    home.style.top = (ty - oY) + 'px';

    /* now the clones sit on the dots, hide the real dots so it's the SAME swatches lifting */
    var cardDots = hideCardDots(card);

    /* fins[0] is the active finish (finish-dots renders it first) */
    var activeFin = fins.length ? fins[0] : null;
    S = {
      card: card, cx: tx, cy: ty, origSrc: origSrc, flipBack: flipBack,
      brand: brandTxt, activeName: activeFin ? activeFin.name : '', activeFin: activeFin,
      headTop: headTop, oX: oX, oY: oY, fins: fins, cardDots: cardDots, sel: -1, closing: false,
      geom: g, plqH: plqH, // measured showcase geometry - commit() reuses it verbatim
      armedAt: Date.now(), demo: false
    };
    setCaption(brandTxt, S.activeName, activeFin); // current finish + its price until they pick another

    /* lock the page + flag for the .is-press handler to stand down */
    document.documentElement.classList.add('fb-on');

    /* play the bloom with a CSS TRANSITION (not a Web-Animation): iOS Safari renders a
       canceled fill Web-Animation's flight TWICE (the "double-play" - proven phone-only,
       one BLOOM, fins fire once). A CSS transition has no cancel/onfinish and iOS renders
       it once. Each clone starts on its dot (inline transform, set above) and transitions
       out to its slot, staggered for the cascade, with a soft overshoot easing. */
    /* DOUBLE rAF: an rAF callback runs BEFORE its frame paints, so starting the
       flight in the first one commits the transition without the "sitting on
       the dot" pose ever reaching the screen - the first painted frame is
       already mid-flight (the jerky lift-off). Waiting one PAINTED frame lets
       the clones render at rest and their textures rasterize, so the lift-off
       reads from stillness. Costs 16ms of latency, imperceptible at a 340ms arm. */
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      if (!S || S.bloomStarted) return;
      S.bloomStarted = true;
      dbg('rAF-bloom #' + (++bloomRaf));
      overlay.classList.add('on');
      focal.style.transition = '';
      focal.style.transform = 'translate(0px,0px) scale(1)';
      fins.forEach(function (f, i) {
        /* EASING: accelerate FROM REST (initial slope ~0.1x), brisk middle,
           slight overshoot landing. The old (.34,1.26,..) curve launched at
           ~3.7x average velocity - most of the flight happened in ~4 frames,
           faster than the eye tracks ("can't see it until it's near my
           finger" - Chris). Objects thrown from rest accelerate; so do these. */
        f.el.style.transition = 'transform ' + BLOOM_MS + 'ms cubic-bezier(.45,.05,.3,1.14) ' + (i * BLOOM_STAGGER) + 'ms';
        f.el.style.transform = 'translate3d(0,0,0) scale(1)';
      });
      /* after the cascade lands, swap to a fast no-delay transition for drag-select */
      window.setTimeout(function () {
        if (!S || S.committed) return;
        for (var j = 0; j < S.fins.length; j++) {
          S.fins[j].el.style.transition = 'transform .2s cubic-bezier(.2,.8,.3,1),box-shadow .2s ease';
        }
      }, BLOOM_MS + (fins.length - 1) * BLOOM_STAGGER + 80);
    }); });
    vibrate(12);
    return true;
  }

  /* ---------- drag: pick the swatch under the thumb's direction ---------- */
  function updateSelection(x, y) {
    if (!S) return;
    var vx = x - S.cx, vy = y - S.cy;
    var dist = Math.sqrt(vx * vx + vy * vy);
    var idx = -1;
    /* only select when the finger is in the ring band AND pointing roughly at a
       swatch - so the centre dead-zone OR drifting off into empty space deselects. */
    if (dist >= DEADZONE && dist <= OUTER_R) {
      var n = nearestByAngle(S.fins, Math.atan2(vy, vx));
      if (n.d <= ANG_TOL) idx = n.idx;
    }
    if (idx === S.sel) return;

    if (S.sel >= 0 && S.fins[S.sel]) {
      var prev = S.fins[S.sel];
      prev.el.classList.remove('sel');
      prev.el.style.transform = 'translate3d(0,0,0) scale(1)';
    }
    S.sel = idx;

    if (idx < 0) {
      /* back in the dead-zone: revert the hero to the original finish */
      setFocalImage(S.origSrc);
      setCaption(S.brand, S.activeName, S.activeFin);
      overlay.classList.remove('has-sel');
    } else {
      var f = S.fins[idx];
      f.el.classList.add('sel');
      f.el.style.transform = 'translate3d(0,0,0) scale(1.22)';
      setFocalImage(f.fimg || S.origSrc);
      setCaption(S.brand, f.name, f);
      overlay.classList.add('has-sel');
      vibrate(8);                   // Android tick on each new swatch; no-op on iOS
    }
  }

  /* ---------- close: navigate to a finish, or glide swatches home ---------- */
  function close(href) {
    if (!S || S.closing) return;
    dbg('close(' + (href ? 'nav' : 'cancel') + ')');
    S.closing = true;
    /* confirmed-hold rule: only a bloom the user actually SAW (open >= CONFIRM_MS,
       or one they navigated from) retires the discovery hints */
    if (!S.demo && (href || Date.now() - S.armedAt >= CONFIRM_MS)) hintsRetire();
    document.documentElement.classList.remove('fb-on');

    if (href) { window.location.href = href; return; }

    /* reverse: hero flies back into the card, and each swatch glides home to its dot
       (staggered, staying visible the whole way) - then the real dots reappear exactly
       as the clones land, so it clearly drops back onto the card. */
    overlay.classList.remove('on', 'has-sel');
    heldCard = S.card; if (heldCard) heldCard.classList.add('fb-hold'); // keep its dots from press-lifting mid-glide
    startGlideScroll(); // anchor overlay to the document so children can ride the scroll
    focal.style.position = 'absolute'; // hero rides the document-anchored overlay (no transform layer)
    if (S.flipBack) focal.style.transform = S.flipBack;
    var fins = S.fins, dd = S.cardDots, maxEnd = 0;
    for (var i = 0; i < fins.length; i++) {
      var f = fins[i];
      f.el.classList.remove('sel');
      f.el.style.position = 'absolute'; // ditto - the clone scrolls natively with the page
      var delay = i * 26, dur = 360;
      maxEnd = Math.max(maxEnd, delay + dur);
      /* glide back to the EXACT transform the bloom started from (lands on the dot) -
         CSS transition, same as the bloom (no Web-Animation = no iOS double-render).
         Only real-dot clones return; overflow (+N) clones fade out. */
      f.el.style.transition = 'transform ' + dur + 'ms cubic-bezier(.4,0,.35,1) ' + delay + 'ms, opacity .3s ease, box-shadow ' + dur + 'ms ease ' + delay + 'ms, border-width ' + dur + 'ms ease ' + delay + 'ms';
      f.el.style.transform = 'translate3d(' + f.dx + 'px,' + f.dy + 'px,0) scale(' + f.dotScale + ')';
      f.el.style.opacity = f.fromCard ? '1' : '0';
      f.el.style.boxShadow = '0 0 0 rgba(0,0,0,0)';          // fade the drop shadow out
      f.el.style.borderWidth = (2 / f.dotScale).toFixed(2) + 'px'; // renders ~2px at the landed scale, matching the dot
    }
    /* once the clones have landed: fade the real dots/chip back in (a re-armed same
       card is skipped so we don't fight it), then remove the clones a beat later so
       they cover the dots until the fade is done - no pop. */
    window.setTimeout(function () {
      if (!(S && S.cardDots === dd)) showCardDots(dd);
      window.setTimeout(function () {
        if (!S && overlay) {
          stopGlideScroll(); // landed + removed: revert the overlay to viewport-fixed
          releaseHeld();      // restore the card's normal press-lift
          var sw = overlay.querySelectorAll('.fb-sw');
          for (var k = 0; k < sw.length; k++) sw[k].remove();
        }
      }, 240);
    }, maxEnd + 20);
    S = null;
  }

  /* ---------- commit: release on a finish freezes into the action modal ---------- */
  function commit(idx) {
    var f = S.fins[idx]; if (!f) return;
    if (!S.demo) hintsRetire();   // committing = fully engaged, hints are done
    S.committed = true; S.sel = idx; S.chosen = f;
    /* ignore the synthetic click iOS fires from this very release (would hit the
       scrim and instantly close); real taps a moment later are honoured. */
    S.ignoreClicks = true;
    window.setTimeout(function () { if (S) S.ignoreClicks = false; }, 340);

    resetTilt(); // the committed hero is a static showcase
    var vw = window.innerWidth, vh = window.innerHeight;
    var oX = S.oX || 0, oY = S.oY || 0; // same fixed-positioning offset as the preview
    /* the EXACT geometry the preview measured (incl. the wrapped-label height)
       -> the hero doesn't move or resize on commit (the FLIP below degenerates
       to identity); the stack metrics come along for the row/tray/strip */
    var g = S.geom || showcaseGeom(S.headTop || 0, S.plqH);
    var hTop = g.hTop, hw = g.hw, capB = g.capB, rowB = g.rowB, trayB = g.trayB, railGap = g.railGap;
    S.railOn = g.railOn;
    var hLeft = (vw - hw) / 2;
    xBtn.style.top = S.headTop > 0 ? (S.headTop + 10 - oY) + 'px' : ''; // keep close below the header

    /* FLIP-move the hero from its preview spot up to the consistent showcase spot */
    var fLeft = hLeft - oX, fTop = hTop - oY;
    var curL = parseFloat(focal.style.left) || 0, curT = parseFloat(focal.style.top) || 0, curW = parseFloat(focal.style.width) || hw;
    focal.style.left = fLeft + 'px'; focal.style.top = fTop + 'px'; focal.style.width = hw + 'px';
    imx.style.height = hw + 'px';
    focal.style.transition = 'none';
    focal.style.transform = 'translate(' + (curL - fLeft) + 'px,' + (curT - fTop) + 'px) scale(' + (curW / hw) + ')';

    /* swatch row stays with the product (just below the plaque object); the
       action grid + range strip anchor toward the BOTTOM (thumb zone), with
       the free space between the two zones - on tight screens the bottom
       cluster rises until it meets the row, collapsing to a simple stack. */
    overlay.classList.toggle('fb-compact', !!g.compact);
    var n = S.fins.length;
    var gap = Math.min(56, (vw - 48) / Math.max(n, 1));
    var rowLeft = vw / 2 - ((n - 1) * gap) / 2, rowTop = hTop + hw + capB;
    var rowBase = rowTop + rowB;                     // bottom of the product zone
    var trayH = g.compact ? 94 : 102;                // 2x2 grid: two rows + gap
    var trayTopY, railTopY;
    if (S.railOn) {
      railTopY = vh - 12 - 176;                      // strip block hugs the bottom
      trayTopY = Math.max(rowBase, railTopY - railGap - trayH);
      railTopY = trayTopY + trayH + railGap;         // tight screens: falls back to stacking
    } else {
      trayTopY = Math.max(rowBase, vh - 12 - trayH);
    }
    tray.style.left = (vw / 2 - oX) + 'px'; tray.style.top = (trayTopY - oY) + 'px';
    if (railEl && S.railOn) railEl.style.top = (railTopY - oY) + 'px';

    requestAnimationFrame(function () {
      focal.style.transition = '';
      focal.style.transform = 'translate(0px,0px) scale(1)';
      S.fins.forEach(function (ff, i) {
        if (ff.anim) { try { ff.anim.cancel(); } catch (e) {} ff.anim = null; }
        ff.rowTx = rowLeft + i * gap; ff.rowTy = rowTop;
        var sel = (i === idx);
        ff.el.style.transition = 'transform .34s cubic-bezier(.2,.8,.3,1),box-shadow .2s ease,translate .5s cubic-bezier(.45,.05,.25,1)'; // ring -> row; translate = the slide-away
        ff.el.style.opacity = '1';
        ff.el.style.transform = 'translate(' + (ff.rowTx - ff.slot.x) + 'px,' + (ff.rowTy - ff.slot.y) + 'px) scale(' + (sel ? 1.15 : .9) + ')';
        ff.el.classList.toggle('sel', sel);
      });
    });

    overlay.classList.add('committed');
    overlay.classList.remove('has-sel');
    setFocalImage(f.fimg || S.origSrc);
    setCaption(S.brand, f.name, f);
    setAddBtn(f);
    syncCommitActions(f);
    vibrate(14);
  }

  /* switch finishes by tapping the swatch row in the committed modal */
  function selectCommitted(i) {
    if (!S || !S.committed || S.sel === i) return;
    var f = S.fins[i]; if (!f) return;
    if (S.sel >= 0 && S.fins[S.sel]) {
      var p = S.fins[S.sel];
      p.el.classList.remove('sel');
      p.el.style.transform = 'translate(' + (p.rowTx - p.slot.x) + 'px,' + (p.rowTy - p.slot.y) + 'px) scale(.9)';
    }
    S.sel = i; S.chosen = f;
    f.el.classList.add('sel');
    f.el.style.transform = 'translate(' + (f.rowTx - f.slot.x) + 'px,' + (f.rowTy - f.slot.y) + 'px) scale(1.15)';
    setFocalImage(f.fimg || S.origSrc);
    setCaption(S.brand, f.name, f);
    setAddBtn(f); syncCommitActions(f); vibrate(8);
  }

  /* dismiss the committed modal (scrim / x / after add) */
  function closeCommitted() {
    if (!S) return;
    if (S.away) { // e.g. resize while the Add-to-Project panel is out
      S.away = false;
      overlay.classList.remove('fb-away');
      var apr = document.getElementById('apaRoot');
      if (apr) apr.classList.remove('apa--bloom');
      apaRequestClose(); // panel closes as a normal drawer (releases its scroll lock)
    }
    document.documentElement.classList.remove('fb-on');
    overlay.classList.remove('on', 'committed', 'has-sel', 'fb-compact');
    var dd = S.cardDots;
    var sw = overlay.querySelectorAll('.fb-sw');
    for (var k = 0; k < sw.length; k++) sw[k].style.opacity = '0';
    window.setTimeout(function () {
      if (!(S && S.cardDots === dd)) showCardDots(dd); // fade the card's real dots back in
      if (!S && overlay) {
        var s = overlay.querySelectorAll('.fb-sw');
        for (var j = 0; j < s.length; j++) s[j].remove();
      }
    }, 300);
    S = null;
  }

  /* Add to cart: get the finish product's variant, then submit a tiny hidden
     /cart/add form - the global atelier-cart interceptor AJAX-adds it and opens
     the cart drawer, exactly like the card's own quick-add. */
  function addToCart(f) {
    var url = (f.href || '').split('?')[0].split('#')[0];
    if (!url) return;
    addBtn.disabled = true; addBtn.textContent = 'Adding…';
    fetch(url + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        /* pick-up-only products (shower screens) need a size AND a pick-up location, both
           chosen on the product page (see atelier-pickup.js) - take the shopper there */
        if (p.tags && p.tags.indexOf('pickup-only') > -1) { addBtn.textContent = 'Opening…'; window.location.href = url; return; }
        var vs = p.variants || [], v = null;
        for (var i = 0; i < vs.length; i++) { if (vs[i].available) { v = vs[i]; break; } }
        if (!v) v = vs[0];
        if (!v) throw 0;
        var form = document.createElement('form');
        form.method = 'post'; form.action = '/cart/add'; form.style.display = 'none';
        var inp = document.createElement('input'); inp.type = 'hidden'; inp.name = 'id'; inp.value = v.id;
        form.appendChild(inp); document.body.appendChild(form);
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
        window.setTimeout(function () { try { form.remove(); } catch (e) {} }, 60);
        closeCommitted(); // let the cart drawer take over
      })
      .catch(function () { addBtn.disabled = false; addBtn.textContent = 'Try again'; });
  }

  /* ==================================================================
     COMMITTED-MODAL ACTIONS beyond the buy pair (2026-07-14):
     Save (wishlist) + Add to project + the "More from the range" strip.
     All three act on the CHOSEN FINISH's product (a sibling of the card's
     product), and every one has a visible tap path elsewhere on the site -
     the hold stays an accelerator, never the only door.
     ================================================================== */
  function handleOf(f) {
    var m = f && f.href ? f.href.match(/\/products\/([^\/?#]+)/) : null;
    return m ? m[1] : '';
  }
  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* called on commit + every committed swatch switch: retarget all row-2
     actions and the range strip at the newly chosen finish */
  function syncCommitActions(f) {
    var h = handleOf(f);
    projBtn.dataset.productHandle = h || '';
    projBtn.disabled = false;
    var pl = projBtn.querySelector('span'); if (pl) pl.textContent = 'Add to project';
    setSaveBtn(h && savedSet && savedSet[h] ? 'saved' : 'idle');
    if (h && savedSet === null) {
      seedSaved(function () {
        if (S && S.committed && S.chosen && savedSet && savedSet[handleOf(S.chosen)]) setSaveBtn('saved');
      });
    }
    if (S.railOn && h) railLoad(h); else railClear();
  }

  /* ---------- Wishlist = SWishlist (the site's wishlist data layer) ---------- */
  function setSaveBtn(state) {
    if (!saveBtn) return;
    saveBtn.classList.toggle('ok', state === 'saved');
    saveBtn.disabled = state === 'busy';
    var lbl = saveBtn.querySelector('span');
    if (lbl) lbl.textContent = state === 'saved' ? 'In wishlist' : state === 'busy' ? 'Adding…' : state === 'fail' ? 'Try again' : 'Wishlist';
  }
  function swReady(cb) {
    if (window.SWishlist && window.SWishlist.addToWishlist) return cb(window.SWishlist);
    var n = 0, iv = window.setInterval(function () {
      if (window.SWishlist && window.SWishlist.addToWishlist) { window.clearInterval(iv); cb(window.SWishlist); }
      else if (++n > 20) { window.clearInterval(iv); cb(null); }
    }, 150);
  }
  /* seed the saved-set once per page so the button can open as "Saved" */
  function seedSaved(cb) {
    var sw = window.SWishlist;
    if (!(sw && sw.getList)) { if (cb) cb(); return; }
    try {
      Promise.resolve(sw.getList()).then(function (l) {
        var a = Array.isArray(l) ? l : (l && l.products) || [];
        savedSet = {};
        for (var i = 0; i < a.length; i++) {
          var it = a[i];
          var h = typeof it === 'string' ? it : (it && (it.handle || it.productHandle || it.product_handle));
          if (h) savedSet[h] = 1;
        }
        if (cb) cb();
      }).catch(function () { if (cb) cb(); });
    } catch (e) { if (cb) cb(); }
  }
  /* toggle: tap = add, tap again while "In wishlist" = remove (card parity) */
  function saveFinish(f) {
    var h = handleOf(f);
    if (!h) return;
    if (savedSet && savedSet[h]) { doUnsave(f, h); return; }
    var sw = window.SWishlist;
    if (sw && sw.addToWishlist) { doSave(sw, f, h); return; }
    setSaveBtn('busy'); // engine not up yet: brief poll, then commit + reward
    swReady(function (sw2) {
      if (!S || !S.committed) return;
      if (!sw2) { setSaveBtn('fail'); return; }
      doSave(sw2, f, h);
    });
  }
  /* un-heart language: badge drops now, the heart reverse-flies from the header
     icon back to the button, and the button only un-fills when it LANDS */
  function doUnsave(f, h) {
    var sw = window.SWishlist;
    if (!(sw && sw.removeFromWishlist)) return;
    try { sw.removeFromWishlist(h); } catch (e) { return; }
    if (savedSet) delete savedSet[h];
    vibrate(8);
    var done = function () {
      if (S && S.chosen && handleOf(S.chosen) === h) setSaveBtn('idle');
    };
    var fx = window.AtelierWishlistFX;
    if (fx && fx.rewardRemove) fx.rewardRemove({ sourceBtn: saveBtn, onLand: done });
    else done();
  }
  /* OPTIMISTIC, the same add language as the card hearts: reward NOW - pulse +
     heart flight to the header icon + badge-on-land + Undo toast, all via the
     shared engine in atelier-wishlist-toast.js (its dedupe swallows SWishlist's
     own added event). The header stays visible above the scrim, so the flight
     has a real destination even inside the modal. Reconcile if the commit fails. */
  function doSave(sw, f, h) {
    var p;
    try { p = sw.addToWishlist(h); } catch (e) { setSaveBtn('fail'); return; }
    savedSet = savedSet || {}; savedSet[h] = 1;
    setSaveBtn('saved'); vibrate(10);
    var fx = window.AtelierWishlistFX;
    if (fx) {
      var ic = saveBtn.querySelector('svg') || saveBtn;
      var r = ic.getBoundingClientRect();
      var tt = S.card ? S.card.querySelector('.pc-tt') : null;
      var bg = '';
      try { bg = getComputedStyle(f.el).backgroundImage; } catch (e2) {}
      fx.rewardAdd({
        x: r.left + r.width / 2, y: r.top + r.height / 2,
        product: { title: f.ptitle || (tt ? tt.textContent.trim() : ''), img: f.fimg || S.origSrc, fin: f.name, swatchBg: bg, handle: h },
        sourceBtn: saveBtn,
        onUndo: function () { // toast Undo already removed it from SWishlist
          if (savedSet) delete savedSet[h];
          if (S && S.chosen && handleOf(S.chosen) === h) setSaveBtn('idle');
        }
      });
    }
    Promise.resolve(p).catch(function () {
      if (savedSet) delete savedSet[h];
      if (S && S.chosen && handleOf(S.chosen) === h) setSaveBtn('fail');
    });
  }

  /* ---------- Add to project = the existing panel (atelier-project-add) ----------
     On PDP + wishlist the shell/assets are server-rendered and the panel's own
     delegated [data-apg-add-to-project] listener opens straight off this tap.
     Everywhere else we lazy-fetch the bundle ONCE via Section Rendering
     (sections/atelier-project-add-lazy.liquid), mount it, then re-tap. */
  function apaReady() {
    return apaState === 2 || (apaState === 0 && !!document.getElementById('apaRoot'));
  }
  function apaLoad() {
    if (apaLoadP) return apaLoadP;
    apaState = 1;
    apaLoadP = fetch(location.pathname + '?section_id=atelier-project-add-lazy', { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw 0; return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var root = doc.getElementById('apaRoot');
        var link = doc.querySelector('link[rel="stylesheet"]');
        var scr = doc.querySelector('script[src]');
        if (!root || !scr) throw 0;
        if (!document.getElementById('apaRoot')) document.body.appendChild(document.importNode(root, true));
        var waits = [];
        if (link) {
          waits.push(new Promise(function (res) {
            var href = link.getAttribute('href');
            if (document.querySelector('link[href="' + href + '"]')) return res();
            var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href;
            l.onload = res; l.onerror = res; // css failure degrades, script failure aborts
            document.head.appendChild(l);
          }));
        }
        waits.push(new Promise(function (res, rej) {
          var s = document.createElement('script'); s.src = scr.getAttribute('src');
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        }));
        return Promise.all(waits);
      })
      .then(function () { apaState = 2; });
    apaLoadP.catch(function () { apaState = 0; });
    return apaLoadP;
  }
  /* ---- integrated bloom mode: the panel joins the hold experience ----
     The committed content slides off LEFT (via the translate property, so the
     swatches' inline transforms are untouched) and the panel slides in from
     the right wearing the .apa--bloom skin (transparent, on our scrim, above
     our overlay). The modal never tears down: closing the panel - its X, our
     scrim, Keep browsing - slides the hold content back in. */
  function enterAway() {
    var root = document.getElementById('apaRoot');
    if (!root || !S) return;
    S.away = true;
    /* start the panel below the scrim-exempt header, so its white chrome
       text always sits on the dark scrim, never on the white header band */
    root.style.setProperty('--fb-apa-top', ((S.headTop || headerBottom() || 2) + 10) + 'px');
    root.classList.add('apa--bloom');
    overlay.classList.add('fb-away');
    watchApaClose();
  }
  function returnFromAway() {
    if (S) S.away = false;
    if (overlay) overlay.classList.remove('fb-away');
    var root = document.getElementById('apaRoot');
    if (root) {
      window.setTimeout(function () { // strip the skin only after the panel's close slide
        if (!(S && S.away)) root.classList.remove('apa--bloom');
      }, 600);
    }
  }
  function apaRequestClose() {
    var x = document.getElementById('apaX');
    if (x) x.click(); else returnFromAway(); // X = the panel's own close path (unlocks its scroll)
  }
  var apaWatched = null;
  function watchApaClose() {
    var pa = document.getElementById('apaPanel');
    if (!pa || apaWatched === pa) return;
    apaWatched = pa;
    new MutationObserver(function () { // any close path: X, Keep browsing, Escape
      if (!pa.classList.contains('on') && S && S.away) returnFromAway();
    }).observe(pa, { attributes: true, attributeFilter: ['class'] });
  }

  function onProjTap(e) {
    /* stopPropagation matters here: the panel's own document-level listener is
       NOT ignoreClicks-guarded, so a synthetic release-click must die with us */
    if (!S || !S.committed || S.ignoreClicks) { e.preventDefault(); e.stopPropagation(); return; }
    if (apaReady()) {
      /* the panel opens itself from this same click (delegated listener);
         we stay open - the hold content slides off left and the panel slides
         in from the right wearing the .apa--bloom lightbox skin */
      enterAway();
      return;
    }
    e.preventDefault(); e.stopPropagation();
    var lbl = projBtn.querySelector('span');
    projBtn.disabled = true; if (lbl) lbl.textContent = 'Loading…';
    apaLoad().then(function () {
      projBtn.disabled = false; if (lbl) lbl.textContent = 'Add to project';
      if (S && S.committed) projBtn.click(); // re-tap: now takes the ready branch above
    }).catch(function () {
      apaLoadP = null; // allow a retry tap to refetch
      projBtn.disabled = false; if (lbl) lbl.textContent = 'Try again';
    });
  }

  /* ---------- "More from the range" strip ----------
     The PDP range-rail section IS the endpoint: fetching it for the chosen
     finish's product returns that range already finish-matched, niche-filtered
     and deduped (sections/atelier-range-rail.liquid). We pluck compact tiles
     from its cards, lightbox-style, plus its View-the-range CTA. */
  function railLoad(h) {
    S.railFor = h;
    var c = railCache[h];
    if (c) { renderRail(c); return; }
    railClear(); // hidden while this product's rail is in flight
    fetch('/products/' + h + '?section_id=atelier_range_rail', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var d = parseRail(html);
        railCache[h] = d;
        if (S && S.committed && S.railFor === h && S.railOn) renderRail(d);
      })
      .catch(function () { railCache[h] = { empty: true }; });
  }
  function parseRail(html) {
    if (!html) return { empty: true };
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var root = doc.querySelector('.atelier-range-rail');
    if (!root) return { empty: true }; // no range tag / thin range -> section renders nothing
    var nm = root.querySelector('.arr-name'), cta = root.querySelector('.arr-cta');
    var cards = root.querySelectorAll('.arr-card'), items = [];
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var link = card.querySelector('.pc-im-link') || card.querySelector('a[href*="/products/"]');
      var img = card.querySelector('.pc-im img');
      var tt = card.querySelector('.pc-tt');
      var pr = card.querySelector('.pc-pr .now') || card.querySelector('.pc-pr');
      var href = link ? link.getAttribute('href') : '';
      var src = img ? (img.getAttribute('src') || img.getAttribute('data-src') || '') : '';
      if (!href || !src) continue;
      items.push({ href: href, img: src, name: tt ? tt.textContent.trim() : '', price: pr ? pr.textContent.trim() : '' });
    }
    if (!items.length) return { empty: true };
    return { name: nm ? nm.textContent.trim() : '', cta: cta ? cta.getAttribute('href') : '', items: items };
  }
  function renderRail(d) {
    if (!d || d.empty || !d.items || !d.items.length) { railClear(); return; }
    railHead.textContent = d.name ? ('More from the ' + d.name + ' range') : 'More from the range';
    if (d.cta) { railCta.style.display = ''; railCta.setAttribute('href', d.cta); }
    else railCta.style.display = 'none';
    var html = '';
    for (var i = 0; i < d.items.length; i++) {
      var it = d.items[i];
      html += '<a class="fb-rc" href="' + escHtml(it.href) + '">' +
        '<span><img src="' + escHtml(it.img) + '" alt="" loading="lazy"></span>' +
        '<b>' + escHtml(it.name) + '</b><em>' + escHtml(it.price) + '</em></a>';
    }
    railStrip.innerHTML = html;
    railStrip.scrollLeft = 0;
    railEl.classList.add('has');
  }
  function railClear() {
    if (!railEl) return;
    railEl.classList.remove('has');
    railStrip.innerHTML = '<i></i>'; // keep the store's div:empty rule off it
  }

  /* ---------- click suppression after an armed gesture ---------- */
  /* iOS may synthesise a click on lift; kill the next one so a release
     doesn't also fire the card link / ATC button under the finger. */
  function suppressNextClick() {
    var kill = function (e) {
      e.preventDefault(); e.stopPropagation();
      document.removeEventListener('click', kill, true);
    };
    document.addEventListener('click', kill, true);
    window.setTimeout(function () { document.removeEventListener('click', kill, true); }, 700);
  }

  /* ---------- touch wiring ---------- */
  var pillPress = false; // this press ended in the "no other finishes" pill - swallow its click
  function onStart(e) {
    demoKill();                          // any touch = they're busy; demo + pending hints stand down
    pillPress = false;
    if (S) return;                       // a session is already running
    if (e.touches && e.touches.length > 1) { cancelArm(); return; }
    var t = e.touches ? e.touches[0] : e;
    var card = t.target && t.target.closest ? t.target.closest('.pc') : null;
    if (!card) { dbg('touchstart: not on a .pc'); return; }
    var nfin = card.querySelectorAll('.pc-fin').length;
    /* VB capability card (.pc-fins[data-vb], atelier-finish-dots.liquid): its
       texture discs are pc-ftx, so nfin is 0 here BY DESIGN - the finishes
       are ONE builder product's options, not siblings, and there is nothing
       to bloom or enrich. A completed hold answers with the builder pill. */
    var vbFins = card.querySelector('.pc-fins[data-vb]');
    /* dot-less card (e.g. /search - Algolia cards omit the fins): fetch the real
       liquid card's fins in the background; they usually land inside the
       long-press window, so this very hold can still bloom (arm re-checks).
       A .pc-nofins marker is the liquid card saying "no siblings, definitive" -
       nothing to fetch. */
    if (!nfin && !vbFins && !card.querySelector('.pc-nofins')) { enrichCard(card); dbg('touchstart: 0 fins - enrichment kicked'); }

    cancelArm();
    pressT = Date.now(); pressCard = card;
    var sx = t.clientX, sy = t.clientY, pc = card;
    dbg('touchstart .pc (fins=' + nfin + ')');
    armTimer = window.setTimeout(function () {
      armTimer = 0;
      var ok = arm(pc, sx, sy); // finger has stayed put for LONGPRESS_MS
      if (ok) { armCount++; hintHide(); }
      /* a completed hold on a VB capability card answers with the truth -
         the finishes exist, they live in the builder (the old path told
         builder customers "No other finishes available", actively wrong).
         Count comes from the container (collection cards) or fbVb (search
         cards, learned via enrichment). */
      else if (vbFins || pc.dataset.fbVb) {
        pillPress = true;
        var vbN = (vbFins && vbFins.getAttribute('data-vb')) || pc.dataset.fbVb;
        hintShow(HINT_IC + vbN + ' finishes available in the Vanity Builder', sx, Math.max(10, sy - 74), 2200);
        vibrate(8);
        dbg('VB capability hold - pill shown (' + vbN + ')');
      }
      /* a completed hold on a KNOWN finish-less product answers instead of
         going dead (Chris: "they hold, and nothing happens - they'll think
         it's broken"). Known = the liquid marker or a resolved enrichment;
         an enrichment still in flight stays silent (the product may well
         have finishes - never claim otherwise). */
      else if (pc.querySelector('.pc-nofins') || pc.dataset.fbNofins === '1') {
        pillPress = true;
        hintShow(HINT_IC + 'No other finishes available', sx, Math.max(10, sy - 74), 2000);
        vibrate(8);
        dbg('finless hold - pill shown');
      }
      dbg(ok ? ('>> BLOOM #' + armCount) : 'arm() false');
    }, LONGPRESS_MS);
  }

  function onMove(e) {
    var t = e.touches ? e.touches[0] : e;
    if (!t) return;
    if (S) {
      /* lock the page scroll the whole time the overlay is up; only the live
         preview phase tracks the thumb (the committed modal is tap-driven).
         EXCEPT the range strip (touch-action:pan-x = that touch can only pan
         the strip) and the slid-in Add-to-Project panel (it body-locks page
         scroll itself, and its list must scroll natively). */
      if (S.committed && e.target && e.target.closest && e.target.closest('.fb-range-s, #apaRoot')) return;
      if (e.cancelable) e.preventDefault();
      if (!S.committed) { updateSelection(t.clientX, t.clientY); applyTilt(t.clientX, t.clientY); }
      return;
    }
    /* pre-arm: if the finger travels, it's a scroll/swipe -> abandon */
    if (armTimer) {
      var dx = t.clientX - (startX || t.clientX), dy = t.clientY - (startY || t.clientY);
      if (Math.abs(dx) > MOVE_CANCEL || Math.abs(dy) > MOVE_CANCEL) {
        dbg('cancel: moved ' + Math.round(dx) + ',' + Math.round(dy) + ' (scroll)');
        cancelArm();
      }
    }
  }

  function onEnd() {
    dbg('touchend');
    if (armTimer) { cancelArm(); hintNearMiss(); return; } // released before arming = a tap (maybe a near-miss)
    /* the hold that ended in the no-finishes pill is a HOLD, not a tap - don't
       let its release-click navigate to the PDP out from under the message */
    if (pillPress) { pillPress = false; suppressNextClick(); return; }
    if (!S || S.committed) return;         // committed modal is driven by taps, not lift
    if (S.sel >= 0) {
      commit(S.sel);     // release ON a finish -> freeze into the action modal
    } else {
      suppressNextClick();
      close(null);       // centre / off-ring -> cancel, glide home
    }
  }

  /* track the press origin for the pre-arm move test */
  var startX = 0, startY = 0;
  function cancelArm() { if (armTimer) { window.clearTimeout(armTimer); armTimer = 0; } }

  document.addEventListener('touchstart', function (e) {
    var t = e.touches ? e.touches[0] : e;
    if (t) { startX = t.clientX; startY = t.clientY; }
    onStart(e);
  }, { passive: true });
  document.addEventListener('touchmove', onMove, { passive: false }); // non-passive: can lock scroll once armed
  document.addEventListener('touchend', onEnd, { passive: true });
  document.addEventListener('touchcancel', function () {
    dbg('TOUCHCANCEL');
    cancelArm(); if (S && !S.committed) close(null); // don't dismiss a committed modal
  }, { passive: true });

  /* if the viewport changes (rotate / scroll-jump), bail cleanly */
  window.addEventListener('resize', function () {
    cancelArm();
    if (S && S.committed) closeCommitted(); else if (S) close(null);
  });

  /* ==================================================================
     DISCOVERABILITY + dot-less-card ENRICHMENT
     (design approved via the mouse-as-finger harness, Chris 2026-07-13)
     L1 demo-once: first collection visit, a ghost finger replays the
       REAL bloom on the first fully-visible card. Once per device;
       cancelled forever this visit by any touch/scroll before it fires.
     L2 near-miss: a 130ms..LONGPRESS press that never armed shows a
       one-time "Tip: hold to preview finishes" pill.
     Stand-down: ONE confirmed hold (bloom open >= CONFIRM_MS, or a
       commit/navigation) retires every hint. Keys are plain
       localStorage, shared across /search + all collections.
     Enrichment: /search cards are .pc WITHOUT fins (no Algolia
       metafields) - first press fetches the product's real liquid card
       via the existing wishlist single-card endpoint and injects its
       .pc-fins hidden (bloom data only; the visible design stays
       dot-free; hidden dots bloom from the card image edge and fade
       home - the engine already handles both).
     ================================================================== */
  var HK_DEMO = 'fbh_demo', HK_MISS = 'fbh_miss', HK_HOLDS = 'fbh_holds';
  function hGet(k) { try { return localStorage.getItem(k) === '1'; } catch (e) { return true; } } // storage blocked -> behave as "seen", never nag
  function hSet(k) { try { localStorage.setItem(k, '1'); } catch (e) {} }
  var pressT = 0, pressCard = null, demoTimer = 0, hintEl = null, hintT = 0, ghostEl = null, demoEl = null;
  var HINT_IC = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2"/><circle cx="12" cy="12" r="8" opacity=".45"/></svg>';

  (function hintCSS() {
    if (document.getElementById('fb-hint-style')) return;
    var st = document.createElement('style'); st.id = 'fb-hint-style';
    st.textContent =
      '.fb-hint{position:fixed;z-index:100001;transform:translate(-50%,6px);background:#1f2125;color:#fff;' +
        'font:500 12.5px/1.2 Sora,system-ui,sans-serif;padding:10px 15px;border-radius:999px;white-space:nowrap;' +
        'box-shadow:0 14px 34px rgba(15,16,18,.4);opacity:0;pointer-events:none;transition:opacity .3s,transform .3s}' +
      '.fb-hint.on{opacity:1;transform:translate(-50%,0)}' +
      '.fb-hint b{font-weight:600}' +
      '.fb-hint svg{width:16px;height:16px;vertical-align:-4px;margin-right:7px;stroke:#fff;fill:none;stroke-width:1.6}' +
      '.fb-ghost{position:fixed;z-index:100000;width:46px;height:46px;border-radius:50%;transform:translate(-50%,-50%);' +
        'background:rgba(196,18,48,.14);border:2px solid rgba(196,18,48,.45);pointer-events:none;opacity:0;transition:opacity .4s}' +
      '.fb-ghost.on{opacity:1}' +
      '.fb-ghost i{position:absolute;inset:-9px;border-radius:50%;border:1.5px solid rgba(196,18,48,.3);animation:fbghp 1.3s ease-out infinite}' +
      '@keyframes fbghp{0%{transform:scale(.75);opacity:.9}100%{transform:scale(1.25);opacity:0}}' +
      /* DOCUMENT-anchored (not fixed): the demo rides the page like content, so a
         scroll never strands it over the wrong cards - it just scrolls away
         (same anchoring trick as the engine's glide-home). */
      /* z 55: above page content, BELOW the sticky header (z 60) - the guide
         scrolls under the header like ordinary page content (Chris). */
      '.fb-demo{position:absolute;left:0;top:0;width:0;height:0;overflow:visible;z-index:55;pointer-events:none;transition:opacity .3s}' +
      '.fb-demo .fb-ghost{position:absolute}' +
      '.fb-demo .fb-hint{position:absolute}' +
      '.fb-demo-sw{position:absolute;width:40px;height:40px;border-radius:50%;border:2.5px solid #fff;' +
        'outline:1px solid rgba(29,31,35,.12);box-shadow:0 6px 18px rgba(0,0,0,.3);will-change:transform}';
    document.head.appendChild(st);
  })();

  function hintShow(html, x, y, ms) {
    if (!hintEl) { hintEl = document.createElement('div'); hintEl.className = 'fb-hint'; document.body.appendChild(hintEl); }
    hintEl.innerHTML = html;
    hintEl.style.left = Math.max(130, Math.min(window.innerWidth - 130, x)) + 'px';
    hintEl.style.top = Math.max(10, y) + 'px';
    hintEl.classList.add('on');
    window.clearTimeout(hintT);
    hintT = window.setTimeout(hintHide, ms || 3500);
  }
  function hintHide() { if (hintEl) hintEl.classList.remove('on'); }
  function hintsRetire() {
    if (!hGet(HK_HOLDS)) dbg('hints RETIRED (confirmed hold)');
    hSet(HK_HOLDS); hintDemoCancel(); hintHide();
  }
  function hintDemoCancel() { if (demoTimer) { window.clearTimeout(demoTimer); demoTimer = 0; } }
  /* touch = they're actively using the page: no later demo ambush this visit.
     A demo that's ALREADY PLAYING is left alone (Chris: it's document-anchored
     and pointer-transparent, so it never interrupts - scroll it away if
     unwanted). Only a real bloom arming dismisses it early (arm -> demoStop). */
  function demoKill() {
    if (!demoDead && (demoTimer || demoSettleT)) dbg('L1 demo pending cancelled (interaction)');
    demoDead = true; hintDemoCancel();
    if (demoSettleT) { window.clearTimeout(demoSettleT); demoSettleT = 0; }
  }
  function demoGhostClear() {
    if (!ghostEl) return;
    ghostEl.classList.remove('on');
    (function (g) { window.setTimeout(function () { g.remove(); }, 400); })(ghostEl);
    ghostEl = null;
  }
  function demoStop() {
    if (demoEl) {
      var d = demoEl; demoEl = null;
      d.style.opacity = '0';
      window.setTimeout(function () { d.remove(); }, 320);
    }
    demoGhostClear(); hintHide();
  }

  /* L2: released like they meant it, before the bloom armed */
  function hintNearMiss() {
    var held = Date.now() - pressT;
    if (!pressCard || held < 130 || held >= LONGPRESS_MS) return;
    if (hGet(HK_HOLDS) || hGet(HK_MISS)) return;
    if (pressCard.querySelectorAll('.pc-fin').length < 2) return;
    hSet(HK_MISS);
    var r = pressCard.getBoundingClientRect();
    hintShow(HINT_IC + 'Tip: <b>hold</b> to preview finishes', r.left + r.width / 2, Math.max(10, r.top - 48), 3800);
    dbg('near-miss tip (' + held + 'ms)');
  }

  /* L1: first-collection-visit demo. A GENERAL GUIDE, not a real bloom (Chris):
     no card is armed, no product hero lifts out, no scrim - just the ghost
     finger and five generic metal swatches fanning out and gliding home. It
     anchors over a card for context but never touches one. */
  var DEMO_FAMS = ['ac-sw--brushed-gold', 'ac-sw--chrome', 'ac-sw--matte-black', 'ac-sw--gunmetal', 'ac-sw--brushed-nickel'];
  function demoCandidate() {
    /* prefer a card with real finish dots (the gesture works right there);
       fall back to any card; press point (55% down) must sit on screen -
       full-card visibility is too strict at phone heights */
    var vh = window.innerHeight, ht = headerBottom();
    var cards = document.querySelectorAll('.pc'), fallback = null;
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var r = c.getBoundingClientRect();
      if (!(r.width > 0 && r.top >= ht + 6 && (r.top + r.height * 0.55) <= vh - 60)) continue;
      if (c.querySelectorAll('.pc-fin').length >= 2) return c;
      if (!fallback) fallback = c;
    }
    return fallback;
  }
  function demoRun() {
    demoTimer = 0;
    if (S || demoEl) return;
    if (document.visibilityState !== 'visible') { dbg('L1 demo: page hidden, skipped'); return; } // no flag burn - tries next visit
    var gx = window.innerWidth / 2, gy = Math.min(window.innerHeight * 0.62, window.innerHeight - 140);
    var card = demoCandidate();
    if (card) { var r = card.getBoundingClientRect(); gx = r.left + r.width / 2; gy = r.top + r.height * 0.55; }
    hSet(HK_DEMO);
    dbg('L1 demo running (generic guide)');
    var mine = document.createElement('div');
    mine.className = 'fb-demo'; mine.innerHTML = '<i></i>';
    document.body.appendChild(mine);
    demoEl = mine;
    /* document-anchor: children are placed at (viewport coord - container origin),
       so they ride the page as it scrolls. Measuring the container's own rect
       also absorbs the store's transformed-ancestor offset (containing-block
       gotcha - same fix as the engine's oX/oY). */
    var cR = mine.getBoundingClientRect(), cX = cR.left, cY = cR.top;
    ghostEl = document.createElement('div'); ghostEl.className = 'fb-ghost'; ghostEl.innerHTML = '<i></i>';
    ghostEl.style.left = (gx - cX) + 'px'; ghostEl.style.top = (gy - cY) + 'px';
    mine.appendChild(ghostEl);
    window.requestAnimationFrame(function () { if (ghostEl) ghostEl.classList.add('on'); });
    var slots = computeSlots(gx, gy, DEMO_FAMS.length, false, headerBottom());
    var sws = [];
    for (var i = 0; i < DEMO_FAMS.length; i++) {
      var el = document.createElement('div');
      el.className = 'fb-demo-sw ' + DEMO_FAMS[i];
      el.innerHTML = '<i></i>'; // div:empty{display:none} store rule
      el.style.left = (slots[i].x - 20 - cX) + 'px';
      el.style.top = (slots[i].y - 20 - cY) + 'px';
      el.style.opacity = '0';
      el.style.transform = 'translate(' + (gx - slots[i].x) + 'px,' + (gy - slots[i].y) + 'px) scale(.35)';
      mine.appendChild(el); sws.push(el);
      void el.offsetWidth; // deterministic transition start (codebase idiom)
      el.style.transition = 'transform .44s cubic-bezier(.45,.05,.3,1.14) ' + (350 + i * 38) + 'ms, opacity .3s ease ' + (350 + i * 38) + 'ms';
      el.style.transform = 'none';
      el.style.opacity = '1';
    }
    /* the pill lives INSIDE the container so it rides the scroll with the rest.
       Horizontally VIEWPORT-centred, not card-centred - centring on a left-
       column card pushed it off the screen edge (Chris). */
    var pill = document.createElement('div');
    pill.className = 'fb-hint';
    pill.innerHTML = HINT_IC + '<b>Hold</b> a product to preview its finishes';
    pill.style.left = (window.innerWidth / 2 - cX) + 'px';
    pill.style.top = (Math.max(10, gy - RING_R - 84) - cY) + 'px';
    mine.appendChild(pill);
    window.setTimeout(function () { if (demoEl === mine) pill.classList.add('on'); }, 750);
    window.setTimeout(function () {         // glide home
      if (demoEl !== mine) return;
      pill.classList.remove('on');
      for (var k = 0; k < sws.length; k++) {
        sws[k].style.transition = 'transform .42s cubic-bezier(.4,0,.35,1) ' + (k * 26) + 'ms, opacity .3s ease ' + (k * 26 + 140) + 'ms';
        sws[k].style.transform = 'translate(' + (gx - slots[k].x) + 'px,' + (gy - slots[k].y) + 'px) scale(.35)';
        sws[k].style.opacity = '0';
      }
    }, 2300);
    window.setTimeout(function () { if (demoEl === mine) demoStop(); }, 3100);
  }
  /* WHEN the demo fires: real shoppers start scrolling immediately, so a
     load-idle timer alone reaches almost nobody (Chris). Fire at the first
     QUIET MOMENT with a card on screen: load +1.6s if untouched, else 1.5s
     after their scrolling SETTLES - within the first 20s of the visit only,
     and never once they've tapped anything (demoKill). The bloom locks page
     scroll while open, so firing mid-scroll is never allowed. */
  var demoDead = false, demoSettleT = 0, demoWindowEnd = 0;
  if (location.pathname.indexOf('/collections/') !== -1 && !hGet(HK_DEMO) && !hGet(HK_HOLDS)) {
    demoWindowEnd = Date.now() + 20000;
    demoTimer = window.setTimeout(demoRun, 1600);
    dbg('L1 demo scheduled (+1.6s idle, or on scroll-settle within 20s)');
    /* Browsers fire scroll events on load for SCROLL RESTORATION (a reload
       jumps back to the old position) - inside the first 700ms that's the
       browser, not the user: re-baseline instead of reacting. */
    var demoY0 = window.scrollY || window.pageYOffset || 0, demoSchedT = Date.now();
    window.addEventListener('scroll', function () {
      var y = window.scrollY || window.pageYOffset || 0;
      if (Date.now() - demoSchedT < 700) { demoY0 = y; return; }
      if (Math.abs(y - demoY0) <= 4) return;
      demoY0 = y;
      if (demoDead || hGet(HK_DEMO) || hGet(HK_HOLDS)) return;
      hintDemoCancel();                                   // never fire while moving
      if (demoSettleT) window.clearTimeout(demoSettleT);
      if (Date.now() > demoWindowEnd) { dbg('L1 demo window over'); return; }
      demoSettleT = window.setTimeout(function () { demoSettleT = 0; demoRun(); }, 1500);
    }, { passive: true });
  } else {
    dbg('L1 demo not scheduled: ' + (location.pathname.indexOf('/collections/') === -1 ? 'not a collection page' : 'flags already set'));
  }

  /* enrichment: give a dot-less .pc real fins data (once per card) */
  function enrichCard(card) {
    if (card.dataset.fbEnrich) return;
    card.dataset.fbEnrich = '1';
    var a = card.querySelector('a[href*="/products/"]');
    var m = a && (a.getAttribute('href') || '').match(/\/products\/([^\/?#]+)/);
    if (!m) { dbg('enrich: no product link'); return; }
    fetch('/products/' + m[1] + '?section_id=atelier-wishlist-card', { headers: { Accept: 'text/html' } })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(function (html) {
        if (!html) return; // fetch hiccup: stay UNKNOWN (never claim "no finishes" on a whim)
        if (card.querySelector('.pc-fin')) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fins = doc.querySelector('.pc-fins');
        if (fins && fins.getAttribute('data-vb')) {
          /* VB capability card fetched for a /search hold: builder options,
             not siblings - keep the count for the honest pill, inject
             nothing (there is nothing to bloom) */
          card.dataset.fbVb = fins.getAttribute('data-vb');
          dbg('enrich: VB capability (' + card.dataset.fbVb + ' finishes)');
          return;
        }
        if (!fins || fins.querySelectorAll('.pc-fin').length < 2) {
          /* the liquid card answered and it has no siblings - remember it so a
             hold on this card can say so (the .pc-nofins marker is the same
             signal server-rendered; older cached sections may lack it, the
             fins check covers those) */
          card.dataset.fbNofins = '1';
          dbg('enrich: no siblings');
          return;
        }
        fins.style.display = 'none'; // bloom data only - the card's visible design stays dot-free
        card.appendChild(fins);
        dbg('enriched ' + m[1] + ' (' + fins.querySelectorAll('.pc-fin').length + ' fins)');
      })
      .catch(function () {});
  }
})();
